import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  PLATFORM_ID,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'tgx-status-view',
  templateUrl: './status-view.component.html',
  styleUrls: ['./status-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StatusViewComponent implements AfterViewInit {
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);

  protected showWidgetFallback = false;

  ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.isLocalhost()) {
      // Local environments are frequently blocked by Widgetbot domain restrictions.
      this.deferFallbackUpdate(true);
      return;
    }

    this.renderWidgetElement();
    this.loadWidgetbotScript();
  }

  private renderWidgetElement(): void {
    const container = document.getElementById('widgetbot-container');
    if (!container) {
      return;
    }

    container.style.width = '100%';
    container.style.minHeight = '600px';

    const widgetElement = document.createElement('widgetbot');
    widgetElement.setAttribute('server', '1121158946074402916');
    widgetElement.setAttribute('channel', '1238058167385067601');
    widgetElement.setAttribute('width', '100%');
    widgetElement.setAttribute('height', '100vh');
    widgetElement.style.display = 'block';
    widgetElement.style.width = '100%';
    widgetElement.style.minHeight = '600px';
    container.replaceChildren(widgetElement);

    // If Widgetbot cannot initialize (domain/network restrictions), avoid blank area.
    window.setTimeout(() => {
      const hasWidgetbotIframe = !!container.querySelector('iframe[src*="widgetbot"], iframe[src*="e.widgetbot.io"]');
      const hasDiscordIframe = !!container.querySelector('iframe[src*="discord"]');
      const hasEmbeddedContent = hasWidgetbotIframe || hasDiscordIframe;

      this.deferFallbackUpdate(!hasEmbeddedContent);
    }, 5000);
  }

  private loadWidgetbotScript(): void {
    const existingScript = document.querySelector('script[data-widgetbot-embed="true"]') as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@widgetbot/html-embed';
    script.setAttribute('data-widgetbot-embed', 'true');
    script.onload = () => {
      this.deferFallbackUpdate(false);

      const widgetbotGlobal = (window as any).widgetbot;
      if (widgetbotGlobal && typeof widgetbotGlobal.register === 'function') {
        widgetbotGlobal.register();
      }

      // Re-run element injection once script is evaluated so tag registration can attach correctly.
      this.renderWidgetElement();

      if (widgetbotGlobal && typeof widgetbotGlobal.register === 'function') {
        widgetbotGlobal.register();
      }
    };
    script.onerror = () => {
      console.error('Widgetbot script failed to load');
      this.deferFallbackUpdate(true);
    };
    document.body.appendChild(script);
  }

  private deferFallbackUpdate(value: boolean): void {
    queueMicrotask(() => {
      this.showWidgetFallback = value;
      this.cdr.detectChanges();
    });
  }

  private isLocalhost(): boolean {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
  }
}
